import { PDFDownloadLink } from '@react-pdf/renderer';
import { ChangeEvent, useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { Button, Header, Loading } from '../../components';
import api from '../../services/api';
import CatalogPDF from './CatalogPDF';
import { ReactComponent as ImageIcon } from '../../assets/icons/image.svg';
import { ReactComponent as ExternalLink } from '../../assets/icons/external-link.svg';
import * as S from './styles';

interface CatalogItem {
  id: number;
  id_ref: number;
  description: string;
  image_url?: string;
  value: number;
  valueFormatted: string;
  reference: string;
  link?: string;
}

let debounceTimer: ReturnType<typeof setTimeout>;

const Catalog = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingPDF, setIsLoadingPDF] = useState(false);
  const [catalogItems, setCatalogItems] = useState<CatalogItem[]>([]);
  const [catalogItemsToPDF, setCatalogItemsToPDF] = useState<CatalogItem[]>([]);
  const [inputValue, setInputValue] = useState('');

  function formatAndReturnData(
    data: Omit<CatalogItem, 'valueFormatted'>[],
  ): CatalogItem[] {
    const formattedData = data.map(item => ({
      ...item,
      valueFormatted: Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: 'BRL',
      }).format(item.value),
    }));

    return formattedData;
  }

  const loadCatalog = useCallback(async () => {
    setIsLoading(true);
    try {
      const { data } = await api.get<CatalogItem[]>('catalog');

      setCatalogItems(formatAndReturnData(data));
    } catch (error) {
      toast.error('Não foi possível consultar os dados. Tente mais tarde.');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadCatalog();
  }, [loadCatalog]);

  const handleSearch = async (event: ChangeEvent<HTMLInputElement>) => {
    setIsLoading(true);
    setIsLoadingPDF(false);
    setInputValue(event.target.value);
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(async () => {
      try {
        const { data } = await api.get<CatalogItem[]>('catalog', {
          params: {
            value: event.target.value,
          },
        });

        setCatalogItems(formatAndReturnData(data));
      } catch (error) {
        toast.error('Não foi possível consultar os dados. Tente mais tarde.');
      } finally {
        setIsLoading(false);
      }
    }, 1500);
  };

  const handleGenerateNewPDF = () => {
    setIsLoadingPDF(true);
    setCatalogItemsToPDF(catalogItems);
  };

  return (
    <>
      <Header />
      <S.Container>
        <S.SearchContainer>
          <S.SearchInput
            placeholder="Buscar por descrição ou referência..."
            value={inputValue}
            onChange={handleSearch}
          />
          <Button
            disabled={!catalogItems.length}
            text="Gerar Novo"
            outlined
            onClick={handleGenerateNewPDF}
          />
          {isLoadingPDF && (
            <PDFDownloadLink
              document={<CatalogPDF data={catalogItemsToPDF} />}
              fileName="catalogo.pdf"
            >
              {({ loading }) =>
                loading ? <Loading color="#fff" /> : 'Baixar PDF'
              }
            </PDFDownloadLink>
          )}
        </S.SearchContainer>
        <S.TableHeader>
          <span>ID</span>
          <span>Descrição</span>
          <span>Referência</span>
          <span>Valor</span>
          <span> </span>
        </S.TableHeader>

        <S.TableBody>
          {isLoading ? (
            <S.EmptyContent>
              <Loading color="#aaa" />
            </S.EmptyContent>
          ) : (
            <>
              {catalogItems.length > 0 ? (
                catalogItems.map(row => (
                  <S.TableRow key={row.id}>
                    <span>{row.id_ref}</span>
                    <span>{row.description}</span>
                    <span>{row.reference}</span>
                    <span>{row.valueFormatted}</span>
                    <S.IconsContainer>
                      <>
                        {row.image_url && (
                          <a
                            href={row.image_url}
                            target="_blank"
                            title="Visualizar imagem"
                            rel="noopener noreferrer"
                          >
                            <ImageIcon height="20" width="20" />
                          </a>
                        )}
                        {row.link && (
                          <a
                            href={row.link}
                            target="_blank"
                            title="Abrir link"
                            rel="noopener noreferrer"
                          >
                            <ExternalLink height="20" width="20" />
                          </a>
                        )}
                      </>
                    </S.IconsContainer>
                  </S.TableRow>
                ))
              ) : (
                <S.EmptyContent>Nenhum item encontrado</S.EmptyContent>
              )}
            </>
          )}
        </S.TableBody>
      </S.Container>
    </>
  );
};

export default Catalog;
